import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import Feedback from '@mui/icons-material/Feedback';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SearchIcon from '@mui/icons-material/Search';
import AppBar from '@mui/material/AppBar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { useFeedbackTrigger } from '@odin/huginn-client/ui';
import PropTypes from 'prop-types';
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { actions } from '../../actions';
import HealthDot from '../../components/BackendStatus/HealthDot';
import { PERMISSIONS, usePermissions } from '../../permissions';
import { EventPanel, getPanelUri } from '../panels';
import NavBreadcrumbs from './NavBreadcrumbs';
import UserMenu from './UserMenu';

// Lazy: pulls in cmdk, only needed once the palette is opened (Cmd/Ctrl+K).
const CommandPalette = lazy(() => import('./CommandPalette/CommandPalette'));

function NavBar(props) {
    const theme = useTheme();

    const {
        isOpen,
        onToggle,
        apiResponseTime,
        apiIsCheckingHealth,
        apiCheckBackendHealth,
        dataResponseTime,
        dataIsCheckingHealth,
        dataCheckBackendHealth,
        refreshInterval,
        nbNotifications,
    } = props;

    const [paletteOpen, setPaletteOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { hasPermission } = usePermissions();
    const canViewEvents = hasPermission(PERMISSIONS.EVENT_VIEW);
    const { open: openFeedback, available: feedbackAvailable } = useFeedbackTrigger();

    // Global Ctrl+K / Cmd+K shortcut
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                setPaletteOpen((prev) => !prev);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Global Ctrl+N / Cmd+N — context-aware "New item" shortcut.
    // Maps the current page to the appropriate creation route.
    useEffect(() => {
        const NEW_ITEM_MAP = [
            { pattern: /^\/readiness/, target: '/readiness/new' },
            {
                pattern: /^\/snapshots/,
                target: '/builder',
                requiresPermission: PERMISSIONS.SNAPSHOT_ADD,
            },
        ];

        const handleNewItem = (e) => {
            // Ignore when typing in an input/textarea/contenteditable
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return;

            if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
                const match = NEW_ITEM_MAP.find(({ pattern }) => pattern.test(location.pathname));
                if (match) {
                    if (match.requiresPermission && !hasPermission(match.requiresPermission)) {
                        return;
                    }
                    e.preventDefault();
                    navigate(match.target);
                }
            }
        };
        document.addEventListener('keydown', handleNewItem);
        return () => document.removeEventListener('keydown', handleNewItem);
    }, [location.pathname, navigate, hasPermission]);

    const onNotificationsClick = () => {
        const newState = {
            status: 'notif',
            type: 'all',
            page: 1,
            start_date: 'null',
            end_date: 'null',
            warning_code: '',
            user: '',
            search: '',
        };

        const panelUri = getPanelUri(<EventPanel />, {}, newState);

        if (location.pathname === getPanelUri(<EventPanel />)) {
            navigate(panelUri, {
                replace: true,
                state: { forceReload: Date.now(), ...newState },
            });
        } else {
            navigate(panelUri, { state: newState });
        }
    };

    return (
        <>
            {paletteOpen && (
                <Suspense fallback={null}>
                    <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
                </Suspense>
            )}
            <AppBar
                position='fixed'
                elevation={0}
                sx={{
                    zIndex: theme.zIndex.drawer + 1,
                    transition: theme.transitions.create(['width', 'margin'], {
                        easing: theme.transitions.easing.easeInOut,
                        duration: 200,
                    }),
                    backdropFilter: 'blur(12px)',
                    backgroundColor: theme.palette.common.navBar,
                    color: theme.palette.text.primary,
                    borderBottom: `1px solid ${theme.palette.common.navBorder}`,
                }}
                data-testid='mimir-header'
            >
                <Toolbar sx={{ minHeight: '56px !important', px: { xs: 1.5, sm: 2 } }}>
                    {/* Left — toggle + app name */}
                    <IconButton
                        onClick={onToggle}
                        edge='start'
                        data-testid='NavbarMenuToggle'
                        sx={{
                            mr: 1.5,
                            color: 'text.secondary',
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                            },
                        }}
                    >
                        {isOpen ? <ChevronLeftRoundedIcon /> : <MenuIcon />}
                    </IconButton>
                    <Typography
                        variant='h6'
                        noWrap
                        sx={{
                            mr: 2,
                            flexShrink: 0,
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            letterSpacing: '-0.02em',
                            color: 'primary.main',
                        }}
                    >
                        Mimir
                    </Typography>

                    {/* Center — breadcrumb trail */}
                    <Box
                        sx={{
                            flexGrow: 1,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <NavBreadcrumbs />
                    </Box>

                    {/* Right — search, health dots, notifications, user menu */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            flexShrink: 0,
                            ml: 1,
                        }}
                    >
                        {/* Global search / command palette */}
                        <Tooltip title='Search pages  (Ctrl+K)'>
                            <IconButton
                                onClick={() => setPaletteOpen(true)}
                                size='small'
                                sx={{
                                    color: 'text.secondary',
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                    },
                                }}
                                aria-label='Open command palette'
                            >
                                <SearchIcon fontSize='small' />
                            </IconButton>
                        </Tooltip>

                        {/* Compact backend health indicators */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mr: 0.5,
                                opacity: 0.8,
                            }}
                        >
                            <HealthDot
                                title='CORE'
                                responseTime={apiResponseTime}
                                isCheckingHealth={apiIsCheckingHealth}
                                checkBackendHealth={apiCheckBackendHealth}
                                refreshInterval={refreshInterval}
                            />
                            <HealthDot
                                title='DATA'
                                responseTime={dataResponseTime}
                                isCheckingHealth={dataIsCheckingHealth}
                                checkBackendHealth={dataCheckBackendHealth}
                                refreshInterval={refreshInterval}
                            />
                        </Box>

                        {/* Notifications — events feed; same permission as /events */}
                        {canViewEvents && (
                            <IconButton
                                onClick={onNotificationsClick}
                                data-testid='NavbarNotificationsIcon'
                                size='small'
                                sx={{
                                    color: 'text.secondary',
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                    },
                                }}
                            >
                                <Badge badgeContent={nbNotifications} color='error'>
                                    <NotificationsIcon fontSize='small' />
                                </Badge>
                            </IconButton>
                        )}

                        <Tooltip title='Send feedback'>
                            <span>
                                <IconButton
                                    onClick={openFeedback}
                                    disabled={!feedbackAvailable}
                                    size='small'
                                    aria-label='Send feedback'
                                    data-testid='FeedbackButton'
                                    sx={{
                                        color: 'text.secondary',
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                        },
                                    }}
                                >
                                    <Feedback fontSize='small' />
                                </IconButton>
                            </span>
                        </Tooltip>

                        {/* User menu — avatar dropdown with theme, admin, logout */}
                        <UserMenu />
                    </Box>
                </Toolbar>
            </AppBar>
        </>
    );
}

NavBar.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
    apiResponseTime: PropTypes.number,
    apiIsCheckingHealth: PropTypes.bool,
    apiCheckBackendHealth: PropTypes.func,
    dataResponseTime: PropTypes.number,
    dataIsCheckingHealth: PropTypes.bool,
    dataCheckBackendHealth: PropTypes.func,
    refreshInterval: PropTypes.number,
    nbNotifications: PropTypes.number,
};

const mapStateToProps = (state) => ({
    apiResponseTime: state.health.responseTime,
    apiIsCheckingHealth: state.health.isFetching,
    nbNotifications: state.health.notif,
    dataResponseTime: state.data_health.responseTime,
    dataIsCheckingHealth: state.data_health.isFetching,
    refreshInterval: state.preference.refreshDelay.health,
});

const mapDispatchToProps = (dispatch) => ({
    apiCheckBackendHealth: () => dispatch(actions.api.health.request()),
    dataCheckBackendHealth: () => dispatch(actions.api.data_health.request()),
});

export default connect(mapStateToProps, mapDispatchToProps)(NavBar);
